import React from 'react';
import styled from 'styled-components/macro';
import { subDays, addDays, isSameDay } from 'date-fns';
import FilterDatePicker from '../_common/FilterDatePicker';
import FilterArrows from 'components/_common/FilterArrows';
import { PbiFilterDaysProps } from './PbiFilterDaysContainer';
import { getDayForDate, getEndDateLimit, getStartDateLimit } from 'helpers/date';
import { ReportTypesNew } from 'models/ReportData';

import {
  DATE_PICKER_NEXT_BUTTON_ELEMENT_ID,
  DATE_PICKER_PREV_BUTTON_ELEMENT_ID,
} from 'KonnectAnalytics/analyticsElementIds';
import { KonnectAnalytics } from 'KonnectAnalytics/konnectAnalytics';
import NewDatePicker from './NewDatePicker';
import { getDateForDayTimeframe } from 'helpers/sharedUtils';
import { disableLeftArrow, disableRightArrow } from 'helpers/datePikcerUtils';

const DaysContainer = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  div::before {
    border-bottom: none !important;
  }
  div::after {
    border-bottom: none;
  }
  input {
    font-weight: 400;
    font-size: 16px;
    color: #354052;
  }
  @media (max-width: 600px) {
    margin-right: 0;
  }
`;

const MobileView = styled.div`
  display: none;
  @media (max-width: 750px) {
    display: block;
    width: 100%;
  }
`;

const PbiFilterDays = React.memo((props: PbiFilterDaysProps) => {
  const {
    lastUpdate,
    selectedDate,
    startDate,
    activeReport,
    isMobile,
    setActiveDate,
    activePrinterStartDate,
    activePrinterEndDate,
  } = props;

  const startDateLimit = getStartDateLimit({
    activeReport,
    activePrinterStartDate,
    startDate,
  });

  const endDateLimit = getEndDateLimit({
    activeReport,
    activePrinterEndDate,
    endDate: lastUpdate,
  });

  const [lowerDate, setLowerDate] = React.useState<Date | null>(null);
  const onChangeActiveDay = (d: Date | undefined) => {
    setActiveDate(d);
  };
  const date = getDateForDayTimeframe({
    lowerDate,
    selectedDate,
    startDateLimit,
    endDateLimit,
  });
  React.useEffect(() => {
    if (lowerDate) setActiveDate(lowerDate);
  }, [lowerDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderPicker = (variant: 'dialog' | 'inline' | 'static', activeReport?: ReportTypesNew) => {
    const lower =
      !!selectedDate && !!endDateLimit && endDateLimit < selectedDate ? endDateLimit : null;
    if (lower && !lowerDate && lowerDate !== lower) setLowerDate(lower);

    return (
      <FilterDatePicker
        data-testid="calendar-day-picker"
        onChange={onChangeActiveDay}
        value={lowerDate || selectedDate || endDateLimit || new Date().toISOString()}
        noHelper
        maxDate={endDateLimit || new Date()}
        minDate={startDateLimit}
        variant={variant}
        disabled={false}
      />
    );
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const disableRight = disableRightArrow({ selectedDate: date, endDateLimit });

  const disableLeft = disableLeftArrow({
    selectedDate: date,
    startDateLimit,
  });

  const onHandleLeft = () => {
    if (!disableLeft) {
      setActiveDate(subDays(selectedDate || endDateLimit || new Date(), 1));
      KonnectAnalytics.decrementDateInDatePicker({
        element_id: DATE_PICKER_PREV_BUTTON_ELEMENT_ID,
        interaction_type: 'click',
      });
    }
  };

  const onHandleRight = () => {
    if (!disableRight) {
      setActiveDate(addDays(selectedDate || endDateLimit || new Date(), 1));
      KonnectAnalytics.incrementDateInDatePicker({
        element_id: DATE_PICKER_NEXT_BUTTON_ELEMENT_ID,
        interaction_type: 'click',
      });
    }
  };

  if (!isMobile) {
    return (
      <NewDatePicker
        activeReport={activeReport}
        timeframe={'day'}
        disableLeft={disableLeft}
        disableRight={disableRight}
        date={date}
        lastUpdate={endDateLimit}
        selectedDate={selectedDate}
        startDate={startDateLimit}
        setActiveDate={setActiveDate}
        onHandleLeft={onHandleLeft}
        onHandleRight={onHandleRight}
      />
    );
  } else {
    return (
      <DaysContainer>
        <MobileView>
          <FilterArrows
            dateTitle={getDayForDate(selectedDate).title}
            handleLeft={onHandleLeft}
            handleRight={onHandleRight}
            disableRight={disableRight}
            disableLeft={disableLeft}
          />
          {renderPicker('static')}
        </MobileView>
      </DaysContainer>
    );
  }
});

export default PbiFilterDays;
